import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import Typography from '@mui/material/Typography';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import { LanguageKeys } from 'src/common/constants/languages';
import {
  columnsDroppableId,
  documentHasSponsorsBlock,
  IPageDocument,
} from 'src/helpers/pageDocumentUtils';
import { documentHasSignupFlowBlock } from 'src/helpers/signupPageLayoutUtils';
import { IColumnsBlock } from 'src/interfaces/PageBlock';
import { IPageColumn } from 'src/interfaces/PageColumn';
import { ITrackMap } from 'src/interfaces/TrackMap';
import AddBlockMenu from 'src/pages/Dashboard/System/Pages/components/AddBlockMenu';
import ColumnAlignControls from 'src/pages/Dashboard/System/Pages/components/ColumnAlignControls';
import BlockDropZone from 'src/pages/Dashboard/System/Pages/components/BlockDropZone';
import BlockListInsertDivider from 'src/pages/Dashboard/System/Pages/components/BlockListInsertDivider';
import SortableBlockCard from 'src/pages/Dashboard/System/Pages/components/SortableBlockCard';
import type { DraggableAttributes } from '@dnd-kit/core';
import { PageBlockType } from 'src/interfaces/PageBlock';

interface ColumnDropZoneProps {
  columnsBlockId: string;
  columnIndex: number;
  layout: IColumnsBlock;
  document: IPageDocument;
  contentKey: string;
  pageContentId?: string;
  selectedEditorLang: LanguageKeys;
  setSelectedEditorLang: (lang: LanguageKeys) => void;
  trackMaps: ITrackMap[];
  onColumnChange: (index: number, patch: Partial<IPageColumn>) => void;
  onAddBlock: (columnIndex: number, type: PageBlockType, insertIndex?: number) => void;
  onUpdate: (id: string, patch: Partial<import('src/interfaces/PageBlock').IPageBlock>) => void;
  onRemove: (id: string) => void;
  onMove: (id: string, direction: 'up' | 'down') => void;
  onColumnsChange: (columnsBlockId: string, patch: Partial<import('src/interfaces/PageBlock').IPageBlock>) => void;
  onDocumentChange: (doc: IPageDocument) => void;
  showColumnDrag: boolean;
  columnSortableAttributes?: DraggableAttributes;
  columnSortableListeners?: Record<string, unknown>;
  isSignupShell?: boolean;
}

export default function ColumnDropZone({
  columnsBlockId: _columnsBlockId,
  columnIndex,
  layout,
  document,
  contentKey,
  pageContentId,
  selectedEditorLang,
  setSelectedEditorLang,
  trackMaps,
  onColumnChange,
  onAddBlock,
  onUpdate,
  onRemove,
  onMove,
  onColumnsChange,
  onDocumentChange,
  showColumnDrag,
  columnSortableAttributes,
  columnSortableListeners,
  isSignupShell = false,
}: ColumnDropZoneProps) {
  const { t } = useTranslation();
  const col = layout.columns[columnIndex]!;
  const hasSponsors = documentHasSponsorsBlock(document);
  const hasSignupFlow = isSignupShell && documentHasSignupFlowBlock(document);
  const addDisabledTypes = {
    sponsors: hasSponsors,
    ...(hasSignupFlow ? { signupFlow: true } : {}),
  };
  const addDisabledTitles = {
    sponsors: hasSponsors ? t('system.pages.sponsorsBlockAlreadyAdded') : undefined,
    signupFlow: hasSignupFlow ? t('system.pages.signupFlowBlockAlreadyAdded') : undefined,
  };

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, flex: 1, minWidth: 0, minHeight: 160 }}
      data-testid={`column-drop-zone-${columnIndex}`}
    >
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}
        data-testid={`column-header-${columnIndex}`}
      >
        {showColumnDrag ? (
          <Box
            {...columnSortableAttributes}
            {...columnSortableListeners}
            sx={{ display: 'flex', cursor: 'grab', touchAction: 'none', color: 'text.secondary' }}
            data-testid={`column-drag-handle-${columnIndex}`}
          >
            <DragIndicatorIcon fontSize="small" />
          </Box>
        ) : null}
        <Typography variant="subtitle2" sx={{ flex: 1 }}>
          {t('system.pages.hero.column', { n: columnIndex + 1 })}
        </Typography>
        <AddBlockMenu
          buttonSize="small"
          showButtonIcon={false}
          buttonLabel={t('system.pages.hero.addBlock')}
          disabledTypes={addDisabledTypes}
          disabledTitles={addDisabledTitles}
          onAdd={type => onAddBlock(columnIndex, type)}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 0.5,
          mb: 1.5,
        }}
        data-testid={`column-toolbar-${columnIndex}`}
      >
        <ColumnAlignControls
          alignH={col.alignH}
          alignV={col.alignV}
          onAlignHChange={alignH => onColumnChange(columnIndex, { alignH })}
          onAlignVChange={alignV => onColumnChange(columnIndex, { alignV })}
        />
      </Box>
      <BlockDropZone droppableId={columnsDroppableId(layout.id, columnIndex)} emptyHint={undefined}>
        {col.blocks.length === 0 ? (
          <BlockListInsertDivider
            disabledTypes={addDisabledTypes}
            disabledTitles={addDisabledTitles}
            onAdd={(type, _columnCount) => onAddBlock(columnIndex, type, 0)}
          />
        ) : (
          col.blocks.map((block, index) => (
            <Fragment key={block.id}>
              <BlockListInsertDivider
                disabledTypes={{ sponsors: hasSponsors }}
                disabledTitles={{
                  sponsors: hasSponsors
                    ? t('system.pages.sponsorsBlockAlreadyAdded')
                    : undefined,
                }}
                onAdd={type => onAddBlock(columnIndex, type, index)}
              />
              <SortableBlockCard
                block={block}
                index={index}
                containerBlockCount={col.blocks.length}
                document={document}
                contentKey={contentKey}
                pageContentId={pageContentId}
                selectedEditorLang={selectedEditorLang}
                setSelectedEditorLang={setSelectedEditorLang}
                trackMaps={trackMaps}
                hasSponsorsBlock={hasSponsors}
                isSignupShell={isSignupShell}
                onUpdate={onUpdate}
                onRemove={onRemove}
                onMove={onMove}
                onColumnsChange={onColumnsChange}
                onDocumentChange={onDocumentChange}
                nested
              />
            </Fragment>
          ))
        )}
        {col.blocks.length > 0 ? (
          <BlockListInsertDivider
            disabledTypes={addDisabledTypes}
            disabledTitles={addDisabledTitles}
            onAdd={type => onAddBlock(columnIndex, type, col.blocks.length)}
          />
        ) : null}
      </BlockDropZone>
    </Paper>
  );
}
